import type { PlayerEntity } from '../entities/PlayerEntity';
import { ITEMS } from '../data/inventory.config';
import { EFFECT_HANDLERS } from '../effect-system/effectHandlers';
import { checkEffectConditions } from '../effect-system/conditionChecker';
import { EffectContext } from '../types/spell.types';
import { EntityEvent } from '../types/buff.types';
import { ItemListener } from '../types/inventory.types';
import { useInventoryStore } from '../store/inventorySlice';
import { dungeonService } from '../services/DungeonService';

interface ResolvedListener {
  listener: ItemListener;
  slot: { itemId: string; slotIndex: number };
}

/**
 * `onDeath` short-circuit: if at any point the player is no longer
 * `isDead()`, iteration stops — avoids consuming several revive items at
 * once. Coupled with priority: a high-priority escape-seed takes
 * precedence over a standard reviver-seed.
 */
export function emitItemListeners(
  player: PlayerEntity,
  event: EntityEvent,
): void {
  // Item listeners read the single local inventory store, so they only apply to
  // the LOCAL player. On the host, a remote player's death must NOT consult the
  // host's inventory (it would revive everyone with the host's seed); a client's
  // own seed is handled client-side (CoopDeathClient.onPlayerDied).
  if (player.id !== dungeonService.localPlayerId) {
    return;
  }
  const slots = useInventoryStore.getState().slots;
  const resolved: ResolvedListener[] = [];
  for (let slotIndex = 0; slotIndex < slots.length; slotIndex++) {
    const slot = slots[slotIndex];
    if (!slot || slot.quantity <= 0) {
      continue;
    }
    const def = ITEMS[slot.itemId];
    if (!def?.listeners) {
      continue;
    }
    for (const listener of def.listeners) {
      if (listener.event !== event) {
        continue;
      }
      if (listener.requiresActivation && !slot.activated) {
        continue;
      }
      resolved.push({
        listener,
        slot: { itemId: slot.itemId, slotIndex },
      });
    }
  }

  // Array.sort is stable since ES2019: at equal priority, insertion order
  // (= `listeners` array order then slot order) is preserved.
  resolved.sort(
    (a, b) => (b.listener.priority ?? 0) - (a.listener.priority ?? 0),
  );

  for (const { listener, slot } of resolved) {
    if (!checkEffectConditions(player, listener.conditions)) {
      continue;
    }
    const handler = EFFECT_HANDLERS[listener.handler];
    if (!handler) {
      continue;
    }
    const ctx: EffectContext = {
      caster: player,
      direction: player.direction,
      affectedCells: [],
    };
    handler(ctx, {
      ...listener.params,
      _itemSlot: slot,
    });
    if (event === 'onDeath' && !player.isDead()) {
      return;
    }
  }
}
